/**
 * MOB-1 · Controles táctiles: joystick virtual + zona de patada.
 *
 * Es un ADAPTADOR más de los que describe el roadmap (§2.2): rellena el mismo
 * `InputState` que el teclado, así que el juego no se entera de por dónde le
 * llega el input y no hay una segunda ruta de código que mantener.
 *
 * Decisiones que vienen de cómo se sostiene un móvil, no del código:
 *
 *  - **El joystick nace donde apoyas el pulgar**, no en un sitio fijo. Un mando
 *    dibujado en una esquina obliga a mirar la pantalla para encontrarlo; uno
 *    que aparece bajo el dedo se usa sin apartar la vista de la pelota.
 *  - **Media pantalla para cada cosa.** La izquierda mueve, la derecha patea —
 *    entera, no solo el botón dibujado. El botón es la señal de dónde mirar; la
 *    zona de acierto es mucho más grande que él, porque en movimiento nadie
 *    acierta a un círculo de 70 px.
 *  - **La patada mantiene la semántica de la barra espaciadora**: mantener carga
 *    y soltar dispara. Es la misma mecánica, no una versión móvil aparte.
 *  - **Multitáctil de verdad**: cada zona sigue SU puntero por id. Mover y
 *    cargar la patada a la vez es el gesto normal, no un caso raro.
 *
 * Sobre convivir con el teclado: cada adaptador escribe en el struct compartido
 * y al soltar limpia solo lo suyo (el joystick, las direcciones; la zona de
 * patada, la patada). Usar los dos a la vez no es un escenario real —y si
 * ocurriera, lo peor que pasa es perder una dirección durante un frame.
 */
import { InputState, ANALOG_SCALE } from "@tak/sim";
import {
  readJoystick, joystickToDirections, joystickToAxis, smoothAxis, knobOffset, JoystickReading,
} from "./joystick";

/**
 * Radio del joystick en px. Marca cuánto hay que estirar el pulgar para ir a
 * tope: más grande da más precisión pero cansa, y tapa más cancha.
 *
 * Bajó de 58 a 44 porque en un teléfono el círculo de 116 px de diámetro se
 * comía media zona de movimiento y obligaba a estirar el pulgar hasta el borde
 * para correr. Con 88 px el gesto entra en el arco natural del pulgar sin
 * levantarlo de la pantalla.
 *
 * No conviene bajarlo mucho más, y la razón no es estética: la zona muerta es
 * una FRACCIÓN del radio (`DEAD_ZONE`, 0,22), así que encogerlo la encoge en
 * píxeles — a 44 px son ~10 px, y por debajo de eso el temblor de un pulgar
 * apoyado empieza a leerse como movimiento. Ahí es cuando el disco deriva solo.
 */
export const STICK_RADIUS = 44;

/**
 * Diámetro del pomo, DERIVADO del radio en vez de escrito a mano.
 *
 * Estaba clavado a 52 px cuando el radio era 58, y ese es justo el emparejamiento
 * que se rompe en silencio: se cambia el radio, el pomo se queda como estaba y
 * pasa a ser casi tan grande como su base. Atado aquí, no puede desincronizarse.
 */
export const KNOB_SIZE = Math.round(STICK_RADIUS * 0.9);

const STYLE_ID = "tak-touch-style";

export const CSS = `
.tc-root{position:fixed;inset:0;z-index:12;pointer-events:none;display:none;
  /* El overlay NO debe tapar la UI: la pausa (z 15) y el chat (z 14) van encima. */
  -webkit-user-select:none;user-select:none;-webkit-tap-highlight-color:transparent;}
.tc-root.on{display:block;}
/* Las zonas arrancan por debajo del marcador y respetan el notch: en un móvil
   con muesca, "abajo del todo" no es el borde físico de la pantalla. */
.tc-zone{position:absolute;top:96px;bottom:0;width:50%;pointer-events:auto;touch-action:none;}
.tc-zone.move{left:0;padding-left:env(safe-area-inset-left,0px);}
.tc-zone.kick{right:0;padding-right:env(safe-area-inset-right,0px);}
.tc-stick{position:absolute;width:${STICK_RADIUS * 2}px;height:${STICK_RADIUS * 2}px;margin-left:${-STICK_RADIUS}px;
  margin-top:${-STICK_RADIUS}px;border-radius:50%;border:2px solid rgba(120,224,208,0.35);
  background:radial-gradient(circle,rgba(34,230,255,0.1),rgba(5,8,12,0.4));
  opacity:0;transition:opacity .12s;pointer-events:none;}
.tc-stick.on{opacity:1;}
.tc-knob{position:absolute;left:50%;top:50%;width:${KNOB_SIZE}px;height:${KNOB_SIZE}px;
  margin-left:${-KNOB_SIZE / 2}px;margin-top:${-KNOB_SIZE / 2}px;border-radius:50%;
  background:radial-gradient(circle at 40% 35%,rgba(34,230,255,0.85),rgba(15,185,214,0.6));
  box-shadow:0 0 16px rgba(34,230,255,0.4);}
.tc-kickbtn{position:absolute;right:calc(26px + env(safe-area-inset-right,0px));
  bottom:calc(34px + env(safe-area-inset-bottom,0px));width:104px;height:104px;border-radius:50%;
  border:2px solid rgba(255,46,99,0.5);background:radial-gradient(circle,rgba(255,46,99,0.16),rgba(5,8,12,0.45));
  display:flex;align-items:center;justify-content:center;color:#ff8f99;font-weight:800;letter-spacing:2px;
  font-size:13px;font-family:"Segoe UI",Tahoma,system-ui,sans-serif;pointer-events:none;transition:transform .08s,border-color .08s;}
.tc-kickbtn.charging{transform:scale(0.92);border-color:#ff2e63;color:#fff;
  background:radial-gradient(circle,rgba(255,46,99,0.4),rgba(5,8,12,0.55));box-shadow:0 0 26px rgba(255,46,99,0.5);}

/* ---- botones de pantalla (chat y zoom) ----
   Van FUERA de las dos zonas de gesto: un botón dentro de la zona de movimiento
   se comería el pulgar que iba a mover el disco. Son los únicos elementos del
   overlay que capturan toques por sí mismos. */
.tc-btn{position:absolute;pointer-events:auto;touch-action:manipulation;display:flex;
  align-items:center;justify-content:center;width:2.7rem;height:2.7rem;padding:0;border:0;
  background:rgba(9,14,20,0.88);color:var(--tb-text,#eef4f8);cursor:pointer;
  font-family:var(--tb-font-ui,system-ui),system-ui,sans-serif;font-size:1.05rem;font-weight:800;
  -webkit-backdrop-filter:blur(0.35rem);backdrop-filter:blur(0.35rem);
  clip-path:polygon(0.45rem 0,100% 0,100% calc(100% - 0.45rem),calc(100% - 0.45rem) 100%,0 100%,0 0.45rem);}
/* Marco que sigue al bisel (misma técnica que .tb-bevel del sistema de diseño:
   un borde normal se lo come el recorte en las esquinas a 45°). */
.tc-btn::after{content:"";position:absolute;inset:0;pointer-events:none;clip-path:inherit;
  padding:1px;background:var(--tb-line-2,rgba(122,160,190,0.28));
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;}
.tc-btn:active{color:var(--tb-cyan,#22e6ff);}
/* Columna izquierda, POR DEBAJO de la línea de fps/red del HUD (que se dibuja
   en el canvas a ~66 px): encima se solapaban dos cosas que no tienen nada que
   ver. El marcador manda en el centro y la pausa en la derecha, así que esta
   esquina es la única libre. */
.tc-chat{top:calc(5.4rem + env(safe-area-inset-top,0px));left:calc(0.75rem + env(safe-area-inset-left,0px));}
/* ZOOM · pareja vertical bajo el chat: en móvil no hay teclas 1/2/3. */
.tc-zoom{left:calc(0.75rem + env(safe-area-inset-left,0px));}
.tc-zoom.in{top:calc(8.6rem + env(safe-area-inset-top,0px));}
.tc-zoom.out{top:calc(11.5rem + env(safe-area-inset-top,0px));}
`;

function injectStyle(): void {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

export interface TouchControls {
  /** Muestra u oculta el overlay (y suelta lo que hubiera pulsado al ocultar). */
  setVisible(visible: boolean): void;
  /** ¿Está el overlay a la vista? */
  readonly visible: boolean;
  /** Suelta todo: se usa al pausar, al escribir en el chat o al perder el foco. */
  releaseAll(): void;
  /** Quita el overlay y sus escuchas. */
  dispose(): void;
}

export interface TouchControlsOptions {
  /** Mientras devuelva true, los gestos se ignoran (p. ej. escribiendo en el chat). */
  isBlocked?: () => boolean;
  /** Radio del joystick en px (por defecto `STICK_RADIUS`). */
  radius?: number;
  /** Abre el chat de cancha (botón de la esquina superior izquierda). */
  onChat?: () => void;
  /** Acerca / aleja la cámara un nivel (botones de zoom). */
  onZoom?: (delta: 1 | -1) => void;
}

/**
 * Monta el overlay táctil y engancha los gestos al `InputState` recibido.
 * Arranca OCULTO: quien llama decide cuándo mostrarlo (ver `shouldShowTouch`).
 */
export function attachTouchControls(
  input: InputState,
  opts: TouchControlsOptions = {},
): TouchControls {
  injectStyle();
  const radius = opts.radius ?? STICK_RADIUS;
  const blocked = (): boolean => opts.isBlocked?.() === true;

  const root = document.createElement("div");
  root.className = "tc-root";

  const moveZone = document.createElement("div");
  moveZone.className = "tc-zone move";
  const stick = document.createElement("div");
  stick.className = "tc-stick";
  const knob = document.createElement("div");
  knob.className = "tc-knob";
  stick.appendChild(knob);
  moveZone.appendChild(stick);

  const kickZone = document.createElement("div");
  kickZone.className = "tc-zone kick";
  const kickBtn = document.createElement("div");
  kickBtn.className = "tc-kickbtn";
  kickBtn.textContent = "PATADA";
  kickZone.appendChild(kickBtn);

  root.appendChild(moveZone);
  root.appendChild(kickZone);

  /** Botón de pantalla del overlay (chat, zoom). */
  const screenBtn = (cls: string, label: string, title: string, onTap: () => void): HTMLElement => {
    const b = document.createElement("button");
    b.className = `tc-btn ${cls}`;
    b.type = "button";
    b.textContent = label;
    b.title = title;
    b.setAttribute("aria-label", title);
    // `pointerdown` y no `click`: en móvil el click llega ~300 ms tarde y, peor,
    // el gesto ya habría empezado a contar como toque de la zona de debajo.
    b.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      onTap();
    });
    root.appendChild(b);
    return b;
  };

  if (opts.onChat) screenBtn("tc-chat", "💬", "Escribir en el chat", () => opts.onChat?.());
  if (opts.onZoom) {
    screenBtn("tc-zoom in", "＋", "Acercar la cámara", () => opts.onZoom?.(1));
    screenBtn("tc-zoom out", "－", "Alejar la cámara", () => opts.onZoom?.(-1));
  }

  document.body.appendChild(root);

  /** Puntero que gobierna el joystick (null = pulgar levantado). */
  let stickPointer: number | null = null;
  let originX = 0;
  let originY = 0;
  /** Puntero que mantiene la patada. */
  let kickPointer: number | null = null;
  let shown = false;

  const clearDirections = (): void => {
    input.up = input.down = input.left = input.right = false;
    input.mx = 0;
    input.my = 0;
  };

  const applyReading = (r: JoystickReading): void => {
    // El EJE es la orden real (media fuerza a medio stick); las teclas van
    // detrás por compatibilidad. Ver `joystickToAxis`.
    // Y se FILTRA: el dedo sobre cristal reporta posiciones con ruido, y ese
    // ruido llegaba al disco como tirones. Ver `smoothAxis`.
    const a = smoothAxis({ mx: input.mx, my: input.my }, joystickToAxis(r, ANALOG_SCALE));
    input.mx = a.mx;
    input.my = a.my;
    const d = joystickToDirections(r);
    input.up = d.up;
    input.down = d.down;
    input.left = d.left;
    input.right = d.right;
    const off = knobOffset(r, radius);
    knob.style.transform = `translate(${off.x}px, ${off.y}px)`;
  };

  const endStick = (): void => {
    stickPointer = null;
    stick.classList.remove("on");
    knob.style.transform = "translate(0px, 0px)";
    clearDirections();
  };

  const endKick = (fire: boolean): void => {
    kickPointer = null;
    kickBtn.classList.remove("charging");
    // Soltar dispara: es la misma semántica que soltar la barra espaciadora.
    // `fire` es false cuando se cancela (pausa, gesto del sistema): ahí NO se
    // dispara, porque el jugador no ha soltado — le han quitado el control.
    if (fire && input.kick) input.kickRelease = true;
    input.kick = false;
  };

  const releaseAll = (): void => {
    endStick();
    endKick(false);
  };

  /* ---------------- joystick ---------------- */

  const onMoveDown = (e: PointerEvent): void => {
    if (blocked()) return;
    if (stickPointer !== null) return; // ya hay un pulgar mandando
    stickPointer = e.pointerId;
    // La captura hace que el resto del gesto siga llegando aquí aunque el dedo
    // se salga de la zona. Sin ella, cruzar al lado de la patada mientras corres
    // deja el disco andando solo con la última dirección.
    moveZone.setPointerCapture?.(e.pointerId);
    const rect = moveZone.getBoundingClientRect();
    originX = e.clientX - rect.left;
    originY = e.clientY - rect.top;
    stick.style.left = `${originX}px`;
    stick.style.top = `${originY}px`;
    stick.classList.add("on");
    applyReading(readJoystick(originX, originY, originX, originY, radius));
    e.preventDefault();
  };

  const onMoveMove = (e: PointerEvent): void => {
    if (stickPointer !== e.pointerId) return;
    if (blocked()) { endStick(); return; }
    const rect = moveZone.getBoundingClientRect();
    applyReading(readJoystick(originX, originY, e.clientX - rect.left, e.clientY - rect.top, radius));
    e.preventDefault();
  };

  const onMoveUp = (e: PointerEvent): void => {
    if (stickPointer !== e.pointerId) return;
    moveZone.releasePointerCapture?.(e.pointerId);
    endStick();
  };

  /* ---------------- patada ---------------- */

  const onKickDown = (e: PointerEvent): void => {
    if (blocked()) return;
    if (kickPointer !== null) return;
    kickPointer = e.pointerId;
    kickZone.setPointerCapture?.(e.pointerId);
    input.kick = true;
    kickBtn.classList.add("charging");
    e.preventDefault();
  };

  const onKickUp = (e: PointerEvent): void => {
    if (kickPointer !== e.pointerId) return;
    kickZone.releasePointerCapture?.(e.pointerId);
    endKick(true);
  };

  const onKickCancel = (e: PointerEvent): void => {
    if (kickPointer !== e.pointerId) return;
    endKick(false);
  };

  moveZone.addEventListener("pointerdown", onMoveDown);
  moveZone.addEventListener("pointermove", onMoveMove);
  moveZone.addEventListener("pointerup", onMoveUp);
  moveZone.addEventListener("pointercancel", onMoveUp);
  kickZone.addEventListener("pointerdown", onKickDown);
  kickZone.addEventListener("pointerup", onKickUp);
  kickZone.addEventListener("pointercancel", onKickCancel);
  // Al irse de la pestaña con el dedo apoyado, no llega ningún `pointerup`: sin
  // esto el disco se queda corriendo solo hasta que alguien vuelva a tocar.
  window.addEventListener("blur", releaseAll);

  return {
    setVisible(visible: boolean): void {
      if (shown === visible) return;
      shown = visible;
      root.classList.toggle("on", visible);
      if (!visible) releaseAll();
    },
    get visible(): boolean { return shown; },
    releaseAll,
    dispose(): void {
      window.removeEventListener("blur", releaseAll);
      releaseAll();
      root.remove();
    },
  };
}

/**
 * ¿Debe verse el mando táctil, según el ajuste y el dispositivo?
 *
 * En "auto" no basta con preguntar si hay pantalla táctil: muchos portátiles la
 * tienen y se juegan con teclado, y aparecerles medio HUD encima sobra. Se
 * exige además que el puntero principal sea BASTO (`pointer: coarse`), que es lo
 * que distingue un dedo de un ratón.
 */
export function shouldShowTouch(
  mode: "auto" | "on" | "off",
  env: { maxTouchPoints: number; coarsePointer: boolean },
): boolean {
  if (mode === "on") return true;
  if (mode === "off") return false;
  return env.maxTouchPoints > 0 && env.coarsePointer;
}

/** Lee del navegador lo que `shouldShowTouch` necesita saber. */
export function detectPointerEnv(): { maxTouchPoints: number; coarsePointer: boolean } {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return { maxTouchPoints: 0, coarsePointer: false };
  }
  return {
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    coarsePointer: window.matchMedia?.("(pointer: coarse)").matches === true,
  };
}
